import { ReactComponent as AddUsersIllustration } from "@app/assets/illustrations/add-users.svg";
import { ReactComponent as DefineResponsiblesIllustration } from "@app/assets/illustrations/define-responsibles.svg";
import { ReactComponent as DiscoverIllustration } from "@app/assets/illustrations/discover.svg";
import { ReactComponent as SetupIllustration } from "@app/assets/illustrations/setup.svg";
import { ReactComponent as UploadDocumentsIllustration } from "@app/assets/illustrations/upload-documents.svg";
import { type OnboardingProgress } from "@app/repositories/LocalStorageRepositories/OnboardingProgressRepository";
import type { AdminPath, ReportingPath } from "@app/routing/adminPaths";
import { adminPaths, reportingPaths } from "@app/routing/adminPaths";
import { type ClassValue } from "@design-system/Utilities";
import { type FC, type SVGProps } from "react";
import { createSearchParams } from "react-router-dom";
import { DashboardPlanStepType } from "../../../generated/client/graphql";

export const modals = {
    uploadDocuments: "uploadDocuments",
};

const configurationCards: {
    id: keyof OnboardingProgress;
    Illustration: FC<SVGProps<SVGSVGElement> & { title?: string | undefined }>;
    bgClassname: ClassValue;
    titleI18nKey: string;
    descriptionI18nKey: string;
    readingTimeI18nKey: string;
    learnMoreCb: {
        fr: () => void;
        en: () => void;
    };
    path?: AdminPath | string;
}[] = [
    {
        id: "discoverBeavr",
        Illustration: DiscoverIllustration,
        bgClassname: "bg-[#5de394]",
        titleI18nKey: "configuration_cards_discover_platform_title",
        descriptionI18nKey: "configuration_cards_discover_platform_description",
        readingTimeI18nKey:
            "configuration_cards_discover_platform_reading_time",
        learnMoreCb: {
            fr: () =>
                window.open(
                    "https://www.loom.com/share/474c1254fa304df699778ffac91420b2?sid=f243942a-3788-4d00-a8b6-fc7b63010793",
                    "_blank",
                ),
            en: () =>
                window.open(
                    "https://www.loom.com/share/a556c975dff442108633c1060b93ac93",
                    "_blank",
                ),
        },
    },
    {
        id: "addUsers",
        Illustration: AddUsersIllustration,
        bgClassname: "bg-[#ffc700]",
        titleI18nKey: "configuration_cards_add_users_title",
        descriptionI18nKey: "configuration_cards_add_users_description",
        readingTimeI18nKey: "configuration_cards_add_users_reading_time",
        learnMoreCb: {
            fr: () =>
                window.open(
                    "https://gethelp.trustditto.com/fr/articles/10802834-ajouter-des-utilisateurs-a-la-plateforme",
                    "_blank",
                ),
            en: () =>
                window.open(
                    "https://gethelp.trustditto.com/en/articles/10802834-adding-users-to-the-platform",
                    "_blank",
                ),
        },
    },
    {
        id: "assignResponsibles",
        Illustration: DefineResponsiblesIllustration,
        bgClassname: "bg-[#f492e4]",
        titleI18nKey: "configuration_cards_assign_responsibles_title",
        descriptionI18nKey:
            "configuration_cards_assign_responsibles_description",
        readingTimeI18nKey:
            "configuration_cards_assign_responsibles_reading_time",
        learnMoreCb: {
            fr: () =>
                window.open(
                    "https://gethelp.trustditto.com/fr/articles/10807838-completer-votre-reporting",
                    "_blank",
                ),
            en: () =>
                window.open(
                    "https://gethelp.trustditto.com/en/articles/10807838-complete-your-reporting",
                    "_blank",
                ),
        },
    },
    {
        id: "configureSustainabilityProfile",
        Illustration: SetupIllustration,
        bgClassname: "bg-[#89b3d0]",
        titleI18nKey:
            "configuration_cards_configure_sustainability_profile_title",
        descriptionI18nKey:
            "configuration_cards_configure_sustainability_profile_description",
        readingTimeI18nKey:
            "configuration_cards_configure_sustainability_profile_reading_time",
        learnMoreCb: {
            fr: () =>
                window.open(
                    "https://gethelp.trustditto.com/fr/articles/10802892-inscriptions-aux-prochains-ateliers-de-travail-webinars",
                    "_blank",
                ),
            en: () => window.open("https://gethelp.beavr.tech/en", "_blank"),
        },
    },
    {
        id: "uploadDocuments",
        Illustration: UploadDocumentsIllustration,
        bgClassname: "bg-[#ec825a]",
        titleI18nKey: "configuration_cards_upload_documents_title",
        descriptionI18nKey: "configuration_cards_upload_documents_description",
        readingTimeI18nKey: "configuration_cards_upload_documents_reading_time",
        learnMoreCb: {
            fr: () => {
                return void 0;
            },
            en: () => {
                return void 0;
            },
        },
        path:
            ".?" +
            createSearchParams({ modal: modals.uploadDocuments }).toString(),
    },
];

export const dittoAdminConfigurationCards = configurationCards;
export const userConfigurationCards = configurationCards.filter(
    (card) => card.id !== "uploadDocuments",
);

type AgendaCards = {
    id: DashboardPlanStepType;
    titleI18nKey: string;
    descriptionI18nKey: string;
    path: AdminPath | `${AdminPath}/${ReportingPath}`;
    learnMoreCb: {
        fr: () => void;
        en: () => void;
    };
}[];

export const getAgendaCards = (
    shouldUseLegacyObjectives?: boolean,
): AgendaCards => {
    return [
        {
            id: DashboardPlanStepType.Reporting,
            titleI18nKey: "agenda_cards_reporting_title",
            descriptionI18nKey: "agenda_cards_reporting_description",
            path: `${adminPaths.reporting}/${reportingPaths.overview}`,
            learnMoreCb: {
                fr: () =>
                    window.open(
                        "https://www.loom.com/share/98a5a5960c72490495a5526e240c05b8?sid=9d718ce4-e0a8-44db-94a3-d347c40eb01f",
                        "_blank",
                    ),
                en: () =>
                    window.open(
                        "https://www.loom.com/share/8dae28cff8664e1e9909f8b37fe85d65?sid=0950872a-1c5f-4422-adb6-6fe3d12d8c5d",
                        "_blank",
                    ),
            },
        },
        {
            id: DashboardPlanStepType.Engagements,
            titleI18nKey: "agenda_cards_engagements_title",
            descriptionI18nKey: "agenda_cards_engagements_description",
            path: shouldUseLegacyObjectives
                ? adminPaths.policies
                : adminPaths.objectives,
            learnMoreCb: {
                fr: () =>
                    window.open(
                        "https://gethelp.trustditto.com/fr/articles/10824234-comment-rediger-ses-engagements-et-objectifs",
                        "_blank",
                    ),
                en: () =>
                    window.open(
                        "https://gethelp.trustditto.com/en/articles/10824234-how-do-you-draw-up-your-commitments-and-objectives",
                        "_blank",
                    ),
            },
        },
        {
            id: DashboardPlanStepType.Policies,
            titleI18nKey: "agenda_cards_policies_title",
            descriptionI18nKey: "agenda_cards_policies_description",
            path: adminPaths.policies,
            learnMoreCb: {
                fr: () =>
                    window.open(
                        "https://gethelp.trustditto.com/fr/articles/10809819-comment-rediger-vos-politiques-dans-ditto",
                        "_blank",
                    ),
                en: () =>
                    window.open(
                        "https://gethelp.trustditto.com/en/articles/10809819-how-to-get-started-with-policies",
                        "_blank",
                    ),
            },
        },
        {
            id: DashboardPlanStepType.Measures,
            titleI18nKey: "agenda_cards_measures_title",
            descriptionI18nKey: "agenda_cards_measures_description",
            path: adminPaths.measures,
            learnMoreCb: {
                fr: () =>
                    window.open(
                        "https://gethelp.trustditto.com/fr/articles/10824289-comment-commencer-le-volet-mesures",
                        "_blank",
                    ),
                en: () =>
                    window.open(
                        "https://gethelp.trustditto.com/en/articles/10824289-how-to-get-started-with-measures",
                        "_blank",
                    ),
            },
        },
        {
            id: DashboardPlanStepType.Followup,
            titleI18nKey: "agenda_cards_followup_title",
            descriptionI18nKey: "agenda_cards_followup_description",
            path: adminPaths.frameworks,
            learnMoreCb: {
                fr: () =>
                    window.open(
                        "https://gethelp.trustditto.com/fr/articles/10809381-guide-soumission-1-2-les-etapes-de-la-soumission-ecovadis",
                        "_blank",
                    ),
                en: () =>
                    window.open(
                        "https://gethelp.trustditto.com/en/articles/10809381-guide-1-2-how-to-submit-your-ecovadis-questionnaire",
                        "_blank",
                    ),
            },
        },
    ];
};
